import type { Source } from './colorScale';
import { sourcesStore } from '../store/sourcesStore';
import { persistentSettingsService } from '../services/persistentSettingsService';
import { persistantStore } from '../store/persistantStore';
import { user } from '../store/userStore';
import { selectedSources, setSelectedSources } from '../store/filterStore';
import { debug, warn } from './console';

const CATALOG_WAIT_MS = 100;
const CATALOG_MAX_ATTEMPTS = 50;

export interface InitializeSourceSelectionsOptions {
  className?: string;
  projectId?: number;
}

function buildProjectContextKey(className: string, projectId: number): string {
  const cn = String(className ?? '').trim();
  const pid = Number(projectId ?? 0);
  if (!cn || !pid) {
    return '';
  }
  return `${cn.toLowerCase()}:${pid}`;
}

function activeProjectContextKey(): string {
  return buildProjectContextKey(
    persistantStore.selectedClassName(),
    persistantStore.selectedProjectId()
  );
}

function contextStillActive(contextKey: string): boolean {
  return contextKey !== '' && contextKey === activeProjectContextKey();
}

/** All catalog source names ordered by source_id. */
export function getAllSourceNames(catalog: readonly Source[]): string[] {
  if (!catalog.length) {
    return [];
  }
  const sorted = [...catalog].sort((a, b) => a.source_id - b.source_id);
  return sorted
    .map((s) => String(s.source_name ?? '').trim())
    .filter(Boolean);
}

/** Normalize and keep only names that exist in the active project catalog. */
export function validateSourceNamesForCatalog(
  rawNames: string[],
  _className: string,
  catalog: readonly Source[]
): string[] {
  if (!Array.isArray(rawNames) || rawNames.length === 0 || !catalog.length) {
    return [];
  }
  return reconcileSelectedSourcesWithProject(rawNames);
}

/** Sync check: project source catalog loaded with at least one source. */
export function isFleetSourceCatalogReady(): boolean {
  if (!sourcesStore.isReady()) {
    return false;
  }
  return sourcesStore.sources().length > 0;
}

/** Wait until sourcesStore is ready for the requested class+project (or context changes). */
export async function waitForSourcesCatalogForContext(
  className: string,
  projectId: number,
  maxAttempts: number = CATALOG_MAX_ATTEMPTS
): Promise<boolean> {
  const requestKey = buildProjectContextKey(className, projectId);
  if (!requestKey) {
    return false;
  }

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (!contextStillActive(requestKey)) {
      debug('waitForSourcesCatalogForContext: context changed while waiting', { requestKey });
      return false;
    }
    if (sourcesStore.isReady()) {
      return true;
    }
    await new Promise((resolve) => setTimeout(resolve, CATALOG_WAIT_MS));
  }

  return contextStillActive(requestKey) && sourcesStore.isReady();
}

/** Row shape for extracting source_name from fleet performance aggregates. */
export interface AggregateSourceRow {
  source_name?: string;
  sourceName?: string;
  SOURCE_NAME?: string;
}

function normalizeSourceName(name: string): string {
  return String(name).toLowerCase().trim();
}

function sourceNameArraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].map(normalizeSourceName).sort();
  const sortedB = [...b].map(normalizeSourceName).sort();
  return sortedA.every((value, index) => value === sortedB[index]);
}

/**
 * Keep only source names that exist in the current project (case-insensitive).
 * Returns canonical names from sourcesStore when matched.
 */
export function reconcileSelectedSourcesWithProject(current: string[]): string[] {
  const trimmed = current
    .map((s) => String(s).trim())
    .filter((s) => s.length > 0);

  if (trimmed.length === 0) {
    return [];
  }

  if (!sourcesStore.isReady()) {
    return trimmed;
  }

  const availableByNormalized = new Map<string, string>();
  for (const source of sourcesStore.sources()) {
    const name = source.source_name;
    if (!name) continue;
    const normalized = normalizeSourceName(name);
    if (!availableByNormalized.has(normalized)) {
      availableByNormalized.set(normalized, String(name));
    }
  }

  const reconciled: string[] = [];
  const seen = new Set<string>();
  for (const raw of trimmed) {
    const normalized = normalizeSourceName(raw);
    const canonical = availableByNormalized.get(normalized);
    if (canonical && !seen.has(normalized)) {
      seen.add(normalized);
      reconciled.push(canonical);
    }
  }

  return reconciled;
}

/**
 * Extract unique source names from aggregate rows (supports API field aliases).
 */
export function extractAggregateSourceNames(rows: AggregateSourceRow[]): string[] {
  const names = new Set<string>();
  for (const row of rows) {
    const name = row.source_name || row.sourceName || row.SOURCE_NAME || '';
    const trimmed = String(name).trim();
    if (trimmed) {
      names.add(trimmed);
    }
  }
  return [...names];
}

/**
 * True when selected sources intersect aggregate row source names (case-insensitive).
 * Returns true when either list is empty (no source-only mismatch to heal).
 */
export function selectedSourcesOverlapAggregateData(
  selected: string[],
  aggregateSourceNames: string[]
): boolean {
  if (selected.length === 0 || aggregateSourceNames.length === 0) {
    return true;
  }
  const dataLower = new Set(aggregateSourceNames.map(normalizeSourceName));
  return selected.some((s) => dataLower.has(normalizeSourceName(s)));
}

/**
 * Reset selectedSources when they do not match any source in returned aggregate data.
 * @returns true when selection was updated
 */
export async function selfHealSourcesFromAggregateData(
  rawRows: AggregateSourceRow[]
): Promise<boolean> {
  const dataSourceNames = extractAggregateSourceNames(rawRows);
  if (dataSourceNames.length === 0) {
    return false;
  }

  const current = selectedSources();
  if (current.length === 0) {
    return false;
  }

  if (selectedSourcesOverlapAggregateData(current, dataSourceNames)) {
    return false;
  }

  let attempts = 0;
  while (!sourcesStore.isReady() && attempts < 10) {
    await new Promise((resolve) => setTimeout(resolve, 100));
    attempts++;
  }

  const reconciled = reconcileSelectedSourcesWithProject(dataSourceNames);
  const toApply = reconciled.length > 0 ? reconciled : dataSourceNames;

  warn('sourceInitialization: selectedSources had no overlap with aggregate data; resetting', {
    previous: current,
    resetTo: toApply,
  });
  setSelectedSources(toApply);
  return true;
}

/**
 * Reconcile filterStore selectedSources with the current project, or initialize defaults.
 * Updates filterStore when stale names are dropped or defaults are applied.
 */
export async function ensureValidSourceSelection(): Promise<string[]> {
  let attempts = 0;
  while (!sourcesStore.isReady() && attempts < 10) {
    await new Promise((resolve) => setTimeout(resolve, 100));
    attempts++;
  }

  const current = selectedSources();
  const reconciled = reconcileSelectedSourcesWithProject(current);

  if (reconciled.length > 0) {
    if (!sourceNameArraysEqual(current, reconciled)) {
      debug('ensureValidSourceSelection: Dropped stale source names', {
        previous: current,
        reconciled,
      });
      setSelectedSources(reconciled);
    }
    return reconciled;
  }

  const initial = await initializeSourceSelections();
  if (initial.length > 0) {
    if (!sourceNameArraysEqual(current, initial)) {
      debug('ensureValidSourceSelection: Initialized default sources', initial);
      setSelectedSources(initial);
    }
    return initial;
  }

  if (current.length > 0) {
    debug('ensureValidSourceSelection: No valid sources; clearing selection');
    setSelectedSources([]);
  }
  return [];
}

/**
 * Initialize source selections from persistent settings. When a user has a saved fleet selection it
 * is validated against the current project and returned; otherwise returns `[]` (no default subset),
 * which the fleet views treat as "all sources".
 *
 * @returns Array of source names (strings) to select; empty array = all sources
 */
export async function initializeSourceSelections(): Promise<string[]> {
  // Wait for sources to be ready
  let attempts = 0;
  while (!sourcesStore.isReady() && attempts < 10) {
    await new Promise(resolve => setTimeout(resolve, 100));
    attempts++;
  }
  
  if (!sourcesStore.isReady()) {
    debug('initializeSourceSelections: sourcesStore not ready');
    return [];
  }
  
  const sources = sourcesStore.sources();
  if (sources.length === 0) {
    debug('initializeSourceSelections: No sources available');
    return [];
  }
  
  // Try to load from persistent settings API
  const currentUser = user();
  if (currentUser?.user_id) {
    const { selectedClassName, selectedProjectId } = persistantStore;
    const className = selectedClassName();
    const projectId = selectedProjectId();
    
    if (className && projectId) {
      try {
        const settings = await persistentSettingsService.loadSettings(
          currentUser.user_id,
          className,
          projectId
        );
        
        if (settings?.fleetPerformanceSources) {
          // Ensure it's an array
          let sources = settings.fleetPerformanceSources;
          if (typeof sources === 'string') {
            try {
              sources = JSON.parse(sources);
            } catch (parseError) {
              debug('initializeSourceSelections: Failed to parse fleetPerformanceSources as JSON:', parseError);
              sources = [];
            }
          }
          
          if (Array.isArray(sources) && sources.length > 0) {
            // Filter out any invalid values and ensure all are strings
            const validSources = sources
              .map(s => String(s).trim())
              .filter(s => s.length > 0);
            
            // Validate that the sources actually exist in the current sources list
            const availableSourceNames = sourcesStore.sources().map(s => s.source_name?.toLowerCase() || '');
            const validatedSources = validSources.filter(sourceName => {
              const normalized = sourceName.toLowerCase();
              return availableSourceNames.includes(normalized);
            });
            
            if (validatedSources.length > 0) {
              debug('initializeSourceSelections: Loaded from persistent settings API', {
                loaded: validSources,
                validated: validatedSources,
                available: sourcesStore.sources().map(s => s.source_name)
              });
              return validatedSources;
            } else {
              debug('initializeSourceSelections: Loaded sources from API but none match available sources', {
                loaded: validSources,
                available: sourcesStore.sources().map(s => s.source_name)
              });
            }
          }
        }
      } catch (error) {
        debug('initializeSourceSelections: Error loading from persistent settings API', error);
      }
    }
  }
  
  // No persisted selection: leave the selection empty. An empty fleet selection means "all sources"
  // everywhere it is consumed (FleetTimeSeries / FleetPerformance / etc.), so fleet views default to
  // the full fleet rather than an arbitrary first-N subset.
  debug('initializeSourceSelections: No persisted selection; defaulting to all sources (empty selection)');
  return [];
}

